import clsx from 'clsx';
import copyToClipboard from 'copy-to-clipboard';
import { useCallback, useEffect, useMemo, useState } from 'react';

import getStore from '$store';
import AddLink from '~components/generic/elements/add-link/AddLink';
import ErrorBoundary from '~components/generic/error-boundary/ErrorBoundary';
import useAppDispatch from '~hooks/useAppDispatch';
import { IEntityComponentProps } from '~interfaces/props';
import ColorMainContainer from '~modules/palette/ColorMainContainer';
import ColorShadeContainer from '~modules/palette/ColorShadeContainer';
import { getRelatedEntityIds } from '~services/entity-helpers';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import { StatusActions } from '~store/actions';
import { selectEntityWithChangesById } from '~store/selectors/custom';

export default function ModulePaletteItem(props: IEntityComponentProps) {
  const dispatch = useAppDispatch();
  const [activeColor, setActiveColor] = useState<string>();

  const colors = useMemo(() => {
    const shades = getRelatedEntityIds(props.data, 'field_shades') || [];
    const mainColor = getRelatedEntityIds(props.data, 'field_main_color') || [];
    return mainColor.concat(shades);
  }, [props?.data]);

  useEffect(() => {
    // Check if active color is one of the available colors
    if (colors.includes(activeColor)) return;
    // Otherwise reset active color
    setActiveColor(
      () =>
        colors.find(color => {
          // check if one of the available colors has the 'default' flag, otherwise use the first one
          const entity = selectEntityWithChangesById(getStore().getState(), color);
          return entity.attributes.field_default || false;
        }) || colors[0],
    );
  }, [activeColor, colors]);

  function handleActiveColorChange(id: string) {
    if (!colors.includes(id)) return;
    setActiveColor(id);
  }

  // Handler: copy asset id to clipboard
  const handleCopyColor = useCallback(
    (color: string) => {
      const isCopied = copyToClipboard(color);
      if (isCopied) {
        dispatch(StatusActions.setSuccessNotification('Copied to clipboard!'));
      }
    },
    [dispatch],
  );

  if (!props.data) {
    return null;
  }
  const { data, editing, onAddNewChild, origData, stable } = props;
  const clsContainer = clsx({
    ...props.className,
    'o-text--white': true,
    'o-grid__cell': true,
    'u-margin-top-md': true,
    'c-color': true,
    container: false,
  });
  const clsContent = clsx('c-color__shades u-margin-top-sm', 'd-flex flex-wrap px-0');
  const childModuleIdsLength = colors.length - 1;

  return (
    <div className={clsContainer} id={getModuleUrlIdFromData(data.id, data.type)}>
      {colors.map((id, i) => id && <div key={i} id={id} />)}
      <ErrorBoundary>
        {activeColor ? (
          <ColorMainContainer
            entityId={activeColor}
            parentContext={data.id}
            parentType={data.type}
            fieldName="relationships.field_main_color.data"
            siblingsLength={childModuleIdsLength}
            onClick={handleCopyColor}
            canMove={false}
            parentIsStable={stable}
          />
        ) : null}
      </ErrorBoundary>
      <div className={clsContent}>
        {colors.map((id, idx) => {
          const index = idx - 1; // reduce index by one because main_color does not count
          const siblingsLength = colors.length - 2;
          const canMove = editing && index >= 0;
          return (
            <ColorShadeContainer
              index={index}
              key={id}
              entityId={id}
              parentContext={data.id}
              parentType={data.type}
              fieldName="relationships.field_shades.data"
              siblingsLength={siblingsLength}
              canMove={canMove}
              onClick={() => handleActiveColorChange(id)}
              isActive={activeColor === id}
              parentIsStable={stable}
            />
          );
        })}
      </div>
      {editing && (
        <AddLink
          onClick={() =>
            onAddNewChild({
              id: origData,
              fieldName: 'relationships.field_shades.data',
              templateType: 'paragraph--color',
            })
          }
        />
      )}
    </div>
  );
}
